
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface ServiceType {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  pricing: string;
  benefits: string[];
  duration: string;
  isExpanded?: boolean;
}

const Services = () => {
  const navigate = useNavigate();
  
  const [services, setServices] = useState<ServiceType[]>([
    {
      id: 'qa-sessions',
      name: 'Q&A Sessions',
      description: 'One-on-one virtual sessions where students can ask alumni specific questions about their experience.',
      longDescription: 'Our Q&A Sessions provide direct access to alumni mentors for personalized guidance. These sessions are tailored to address your specific questions about college applications, career paths, internship opportunities, and more. You\'ll get candid insights from someone who\'s walked the path you\'re considering.',
      pricing: '$30-$100 per 30 minutes',
      benefits: [
        'Direct one-on-one conversations with experienced alumni',
        'Personalized advice tailored to your specific situation',
        'Flexible scheduling to accommodate your availability',
        'Secure video conferencing platform',
        'Option to record sessions for future reference',
      ],
      duration: '30 or 60 minutes',
      isExpanded: false
    },
    {
      id: 'resume-review',
      name: 'Resume Review',
      description: 'Professional resume critique and feedback from alumni in your target industry.',
      longDescription: 'Our Resume Review service connects you with alumni professionals who will provide detailed feedback on your resume. They\'ll help you highlight relevant experiences, use industry-specific keywords, and format your resume to stand out to recruiters and admissions officers.',
      pricing: '$45-$120',
      benefits: [
        'Detailed written feedback on your resume',
        'Industry-specific insights and keywords',
        'Guidance on formatting and structure',
        'Suggestions for highlighting relevant experiences',
        'Tips for addressing potential red flags',
      ],
      duration: '3-5 business days turnaround',
      isExpanded: false
    },
    {
      id: 'essay-review',
      name: 'Essay Review',
      description: 'Comprehensive feedback on application essays from alumni who succeeded at your target schools.',
      longDescription: 'Our Essay Review service pairs you with alumni from your target schools who will provide constructive feedback on your application essays. They\'ll help you craft compelling narratives that showcase your unique qualities while addressing the specific prompts and expectations of each institution.',
      pricing: '$50-$150 per essay',
      benefits: [
        'Line-by-line commentary and suggestions',
        'Insights on what admissions committees look for',
        'Feedback on tone, structure, and content',
        'Advice on how to make your story compelling',
        'Tips for addressing school-specific prompts',
      ],
      duration: '3-5 business days turnaround',
      isExpanded: false
    },
    {
      id: 'case-prep',
      name: 'Case Preparation',
      description: 'Mock case interviews and preparation strategies from alumni at top consulting firms.',
      longDescription: 'Our Case Preparation service helps you prepare for case interviews with guided practice sessions led by alumni from top consulting firms. You\'ll learn proven frameworks, practice with real case examples, and receive personalized feedback to improve your performance and confidence.',
      pricing: '$75-$200 per session',
      benefits: [
        'Practice with realistic case scenarios',
        'Learn industry-standard frameworks and approaches',
        'Receive feedback on your analytical thinking',
        'Develop structured communication skills',
        'Build confidence through multiple practice rounds',
      ],
      duration: '60-90 minutes per session',
      isExpanded: false
    },
    {
      id: 'test-prep',
      name: 'Test Preparation',
      description: 'Customized test prep strategies and tutoring from high-scoring alumni.',
      longDescription: 'Our Test Preparation service connects you with alumni who have mastered standardized tests like the SAT, ACT, GMAT, GRE, LSAT, and MCAT. They\'ll share their personal strategies, provide targeted practice, and help you develop a customized study plan to achieve your target score.',
      pricing: '$40-$120 per hour',
      benefits: [
        'Personalized study plans based on your strengths and weaknesses',
        'Expert strategies from top scorers',
        'Practice with official and custom test materials',
        'Time management and stress reduction techniques',
        'Regular progress assessments and adjustments',
      ],
      duration: 'Flexible hourly sessions',
      isExpanded: false
    },
  ]);

  const toggleExpand = (id: string) => {
    setServices(services.map(service => 
      service.id === id 
        ? { ...service, isExpanded: !service.isExpanded } 
        : service
    ));
  };

  const handleBrowse = (service: ServiceType) => {
    // Navigate to browse page with search parameter
    navigate(`/browse?search=${encodeURIComponent(service.name)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white font-montserrat">
      <NavBar />
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 font-montserrat mb-4">Our Services</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with experienced alumni who can provide valuable insights and guidance for your academic and professional journey.
            </p>
          </div>

          <div className="space-y-6">
            {services.map((service) => (
              <div key={service.id} id={service.id} className="scroll-mt-24">
                <Card className="overflow-hidden border border-gray-200 bg-white">
                  <div 
                    className="flex justify-between items-center p-6 cursor-pointer"
                    onClick={() => toggleExpand(service.id)}
                  >
                    <h2 className="text-xl font-semibold text-gray-900">{service.name}</h2>
                    <Button variant="ghost" size="icon">
                      {service.isExpanded ? <ChevronUp /> : <ChevronDown />}
                    </Button>
                  </div>
                  
                  <CardContent className={`pt-0 px-6 pb-6 ${service.isExpanded ? 'block' : 'hidden'}`}>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-700 mb-6">{service.longDescription}</p>
                      
                      <div className="grid sm:grid-cols-2 gap-6 mb-6">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Pricing</h3>
                          <p className="text-crimson font-medium">{service.pricing}</p>
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Duration</h3>
                          <p className="text-gray-700">{service.duration}</p>
                        </div>
                      </div>
                      
                      <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Benefits</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          {service.benefits.map((benefit, index) => (
                            <li key={index}>{benefit}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <Button 
                        className="w-full sm:w-auto bg-crimson hover:bg-crimson-dark"
                        onClick={() => handleBrowse(service)}
                      >
                        Browse Alumni for {service.name}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Services;
